import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff } from 'lucide-react';

const MessageInput = ({ onSendMessage, disabled }) => {
  const [message, setMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition if available
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event?.resultIndex; i < event?.results?.length; i++) {
          const transcript = event?.results?.[i]?.[0]?.transcript;
          if (event?.results?.[i]?.isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        setMessage((prev) => prev + finalTranscript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event?.error);
        setIsListening(false);
      };
    }
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (message?.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
      if (textareaRef?.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e?.target?.value);
    // Auto-resize textarea
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef?.current?.scrollHeight, 200)}px`;
    }
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef?.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef?.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef?.current?.start();
      setIsListening(true);
    }
  };

  const suggestionChips = [
    'Create a todo app',
    'Build an e-commerce site',
    'Design a portfolio website',
    'Make a blog platform',
  ];

  return (
    <div className="space-y-3">
      {/* Suggestion Chips */}
      {message?.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {suggestionChips?.map((chip, index) => (
            <button
              key={index}
              onClick={() => setMessage(chip)}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              disabled={disabled}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
      {/* Input Area */}
      <form onSubmit={handleSubmit} className="relative">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder="Describe the app you want to build... (e.g., 'A task management app with teams and real-time collaboration')"
          disabled={disabled}
          rows={1}
          className="w-full px-4 py-3 pr-24 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ minHeight: '52px', maxHeight: '200px' }}
        />

        <div className="absolute right-2 bottom-2 flex gap-2">
          {/* Voice Input Button */}
          {recognitionRef?.current && (
            <button
              type="button"
              onClick={toggleVoiceInput}
              className={`p-2 rounded-lg transition-colors ${
                isListening
                  ? 'bg-red-100 text-red-600 hover:bg-red-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              disabled={disabled}
              aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}

          {/* Send Button */}
          <button
            type="submit"
            disabled={!message?.trim() || disabled}
            className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
      {/* Voice Input Indicator */}
      {isListening && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
          <span>Listening... Speak now</span>
        </div>
      )}
    </div>
  );
};

export default MessageInput;